import './ModeledItem.css'

import { FC, useMemo } from 'react'

import { Box, Flex, Text } from '@chakra-ui/react'
import { InspectionArea, InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { CoverThicknessAggregate } from '@editor/interfaces/project'
import { PlaneSide, Shapes } from '@editor/interfaces/shape'
import PolygonDiagram from '@editor/pages/projects/inspection-sheet/components/PolygonDiagram'
import { findLowerPlane, findUpperPlane } from '@editor/services/Shape'
import { RootState, useAppDispatch } from '@editor/store/app'
import { getContrastingColor } from '@uiw/react-color'
import { debounce } from 'lodash'
import { useSelector } from 'react-redux'

import { DEFAULT_TOLERANCE_TYPE, DIAGRAM_WIDTH, GRID_UNIT } from '../../constants'
import { patchEditedInspectionItems } from '../../store'
import { getSelectedCoverThickness, getTableWidths } from '../../utils'
import GridRow from '../GridRow'
import Metric from '../Metric/Metric'
import PolylineDiagram from '../PolylineDiagram'
import PopoverEditing from '../PopoverEditing'
import TableHeader from '../TableHeader'
import GroupName from './GroupName'

/**
 * Check if the item is in the correct plane.
 * If volume, it should be in the upper plane.
 * If plane, it should be in the lower plane.
 *
 * @param item
 * @param shapes
 * @returns
 */
const isCorrectPlane = (item: InspectionItem, shapes: Shapes) =>
  shapes.polygons.find(
    (shape) =>
      shape.shape_id === item.shape_ids.polygons[0] &&
      ((item.volume_id && shape.plane_side === PlaneSide.UPPER) ||
        (!item.volume_id && shape.plane_side === PlaneSide.LOWER)),
  )

const debouncedPatchInspectionItem = debounce(
  (dispatch: ReturnType<typeof useAppDispatch>, inspectionItem: Partial<InspectionItem>) => {
    dispatch(patchEditedInspectionItems([inspectionItem]))
  },
  200,
)

const ModeledItem: FC<{
  /**
   * Title of the volume group
   */
  title: string

  /**
   * If title is empty, use this as the default title.
   */
  defaultTitle: string

  /**
   * Inspection area of these set of inspection items
   */
  inspectionArea: InspectionArea

  /**
   * All inspection items in the volume group
   */
  inspectionItems: InspectionItem[]

  /**
   * Shapes of the project
   */
  shapes: Shapes

  /**
   * Inspection group this item belongs to.
   */
  group?: InspectionGroup

  /**
   * All additional metrics' toggle state, including ones not belonging to this metric.
   */
  allAdditionalMetricsToggle?: boolean[]
}> = ({ title, defaultTitle, inspectionArea, inspectionItems, shapes, group, allAdditionalMetricsToggle = [] }) => {
  // Store
  const dispatch = useAppDispatch()
  const settings = useSelector((state: RootState) => state.inspectionSheet.settings)
  const isAllowedToModifySheet = useSelector((state: RootState) => state.inspectionSheet.isAllowedToModifySheet)
  const groups = useSelector((state: RootState) => state.inspectionGroup.groups)

  // Derived values
  const volume = inspectionItems.find((item) => item.item_type === InspectionItemType.Volume)
  const area = inspectionItems.find(
    (item) => item.item_type === InspectionItemType.PolygonArea && isCorrectPlane(item, shapes),
  )
  const perimeter = inspectionItems.find(
    (item) => item.item_type === InspectionItemType.PolygonPerimeter && isCorrectPlane(item, shapes),
  )
  const length1 = inspectionItems.find(
    (item) => item.item_type === InspectionItemType.PolygonLength1 && isCorrectPlane(item, shapes),
  )
  const length2 = inspectionItems.find(
    (item) => item.item_type === InspectionItemType.PolygonLength2 && isCorrectPlane(item, shapes),
  )
  const vertexLengths = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.EdgeLength)
    .sort((a, b) => (a.edge_length?.index ?? 0) - (b.edge_length?.index ?? 0))
  const grid = inspectionItems.find((item) => item.item_type === InspectionItemType.Grid)
  const upperDepth = inspectionItems.find(
    (item) =>
      item.item_type === InspectionItemType.PlaneToCylindersDistance &&
      shapes.polygons?.find(
        (shape) => shape.shape_id === item.shape_ids.polygons[0] && shape.plane_side === PlaneSide.UPPER,
      ),
  )
  const lowerDepth = inspectionItems.find(
    (item) =>
      item.item_type === InspectionItemType.PlaneToCylindersDistance &&
      shapes.polygons?.find(
        (shape) => shape.shape_id === item.shape_ids.polygons[0] && shape.plane_side === PlaneSide.LOWER,
      ),
  )
  const polylineLength = inspectionItems.find((item) => item.item_type === InspectionItemType.PolylineLength)
  const polylineWidth = inspectionItems.find((item) => item.item_type === InspectionItemType.PolylineWidth)

  const topPlane = inspectionItems[0] ? findUpperPlane(shapes, inspectionItems[0]) : null
  const bottomPlane = inspectionItems[0] ? findLowerPlane(shapes, inspectionItems[0]) : null

  const maxWidth = useMemo(
    () => getTableWidths(allAdditionalMetricsToggle, settings),
    [settings, allAdditionalMetricsToggle],
  )

  return (
    <Box borderBottom={3} borderStyle="solid" borderColor="gray2.300">
      <Flex
        style={{ '--group-color': group ? group.color : 'var(--chakra-colors-primary-50)' } as React.CSSProperties}
        className="modeled-items data-rows"
        borderLeft="12px solid var(--group-color)"
        data-group-id={group ? group.group_id : 'ungrouped'}
        transition="border-color 0.1s ease-in-out"
      >
        <Flex
          className="title"
          backgroundColor="var(--group-color)"
          color={group ? getContrastingColor(group.color) : undefined}
          transition="background-color 0.1s ease-in-out, color 0.3s ease-in-out"
        >
          {group ? <GroupName group={group} groups={groups} isAllowedToModify={isAllowedToModifySheet} /> : null}
          {isAllowedToModifySheet ? (
            <PopoverEditing
              value={title}
              defaultValue={defaultTitle}
              onChange={(value) => {
                debouncedPatchInspectionItem(dispatch, {
                  inspection_item_id: inspectionItems[0].inspection_item_id,
                  inspection_area_id: inspectionItems[0].inspection_area_id,
                  inspection_sheet_id: inspectionItems[0].inspection_sheet_id,
                  part_name: value,
                })
              }}
            />
          ) : (
            <Flex gap={2} className="popover-editing">
              <Text>{title || defaultTitle}</Text>
            </Flex>
          )}
        </Flex>

        <Flex flexDirection="column" w="100%">
          <TableHeader allAdditionalMetricsToggle={allAdditionalMetricsToggle} />

          <Flex width={`${maxWidth.max}px`} bg="white">
            {/* Data */}
            <Flex
              flexDirection="column"
              bg="white"
              alignSelf="stretch"
              borderRightWidth={1}
              borderRightColor="gray2.300"
            >
              {length1?.polygon_length_1 && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={length1}
                  inspectionItemValue={length1.polygon_length_1}
                  title="辺の長さ1"
                  unit="mm"
                  isHidden={!settings.sheet_rows_visibility?.polygon_length_1 || false}
                  toleranceType={settings.sheet_rows_tolerance_type?.polygon_length_1 || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {length2?.polygon_length_2 && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={length2}
                  inspectionItemValue={length2.polygon_length_2}
                  title="辺の長さ2"
                  unit="mm"
                  isHidden={!settings.sheet_rows_visibility?.polygon_length_2 || false}
                  toleranceType={settings.sheet_rows_tolerance_type?.polygon_length_2 || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {vertexLengths.length > 0 &&
                vertexLengths.map(
                  (vertexLength) =>
                    vertexLength.edge_length && (
                      <Metric
                        key={`metrics_edge_length_${vertexLength.inspection_item_id}`}
                        allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                        inspectionItem={vertexLength}
                        inspectionItemValue={vertexLength.edge_length}
                        title={
                          vertexLength.edge_length?.index !== undefined
                            ? `L${vertexLength.edge_length.index + 1}`
                            : `頂点間の長さ`
                        }
                        unit="mm"
                        isHidden={!settings.sheet_rows_visibility?.edge_length || false}
                        toleranceType={settings.sheet_rows_tolerance_type?.edge_length || DEFAULT_TOLERANCE_TYPE}
                      />
                    ),
                )}

              {perimeter?.polygon_perimeter && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={perimeter}
                  inspectionItemValue={perimeter.polygon_perimeter}
                  title="外周長"
                  unit="mm"
                  isHidden={!settings.sheet_rows_visibility?.polygon_perimeter}
                  toleranceType={settings.sheet_rows_tolerance_type?.polygon_perimeter || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {area?.polygon_area && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={area}
                  inspectionItemValue={area.polygon_area}
                  title="面積"
                  unit="m²"
                  isHidden={!settings.sheet_rows_visibility?.polygon_area}
                  toleranceType={settings.sheet_rows_tolerance_type?.polygon_area || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {volume?.volume && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={volume}
                  inspectionItemValue={volume.volume}
                  title="体積"
                  unit="m³"
                  isHidden={!settings.sheet_rows_visibility?.volume}
                  toleranceType={settings.sheet_rows_tolerance_type?.volume || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {grid?.grid && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={grid}
                  inspectionItemValue={grid.grid}
                  title="深さ"
                  unit={GRID_UNIT}
                  isHidden={!settings.sheet_rows_visibility?.grid}
                  additionalMetrics={[
                    {
                      typeKey: 'min_grid_depth',
                      pillColor: 'blue',
                      title: '最小',
                      value: grid.grid.min_grid_depth,
                      isHidden:
                        !settings.sheet_rows_visibility?.grid || !settings.sheet_rows_visibility?.min_grid_depth,
                    },
                    {
                      typeKey: 'max_grid_depth',
                      pillColor: 'green',
                      title: '最大',
                      value: grid.grid.max_grid_depth,
                      isHidden:
                        !settings.sheet_rows_visibility?.grid || !settings.sheet_rows_visibility?.max_grid_depth,
                    },
                  ]}
                  toleranceType={settings.sheet_rows_tolerance_type?.grid || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {upperDepth?.plane_to_cylinders_distance && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={upperDepth}
                  inspectionItemValue={{
                    ...upperDepth.plane_to_cylinders_distance,
                    estimated_value: getSelectedCoverThickness(
                      upperDepth.plane_to_cylinders_distance,
                      settings.cover_thickness_value_type?.upper ?? CoverThicknessAggregate.Min,
                    ),
                  }}
                  title="表かぶり厚"
                  unit="mm"
                  isHidden={!settings.sheet_rows_visibility?.upper_plane_to_cylinders_distance}
                  toleranceType={
                    settings.sheet_rows_tolerance_type?.upper_plane_to_cylinders_distance || DEFAULT_TOLERANCE_TYPE
                  }
                />
              )}
              {lowerDepth?.plane_to_cylinders_distance && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={lowerDepth}
                  inspectionItemValue={{
                    ...lowerDepth.plane_to_cylinders_distance,
                    estimated_value: getSelectedCoverThickness(
                      lowerDepth.plane_to_cylinders_distance,
                      settings.cover_thickness_value_type?.lower ?? CoverThicknessAggregate.Min,
                    ),
                  }}
                  title="裏かぶり厚"
                  unit="mm"
                  isHidden={!settings.sheet_rows_visibility?.lower_plane_to_cylinders_distance}
                  toleranceType={
                    settings.sheet_rows_tolerance_type?.lower_plane_to_cylinders_distance || DEFAULT_TOLERANCE_TYPE
                  }
                />
              )}
              {polylineLength?.polyline_length && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={polylineLength}
                  inspectionItemValue={polylineLength.polyline_length}
                  title="延長"
                  unit="mm"
                  isHidden={false}
                  toleranceType={settings.sheet_rows_tolerance_type?.polyline_length || DEFAULT_TOLERANCE_TYPE}
                />
              )}
              {polylineWidth?.polyline_width && (
                <Metric
                  allAdditionalMetricsToggle={allAdditionalMetricsToggle}
                  inspectionItem={polylineWidth}
                  inspectionItemValue={polylineWidth.polyline_width}
                  title="幅"
                  unit="mm"
                  convertUnit={false}
                  valueOnly
                  isHidden={false}
                  toleranceType={settings.sheet_rows_tolerance_type?.polyline_length || DEFAULT_TOLERANCE_TYPE}
                />
              )}
            </Flex>

            {/* Diagrams  */}
            {bottomPlane && inspectionItems[0]?.inspection_item_id && (
              <Flex justifyContent="center" bg="white" alignSelf="stretch">
                <Box
                  className={[
                    'column',
                    'diagram',
                    !settings.sheet_diagram_visibility?.plane_diagram ? 'hidden' : '',
                  ].join(' ')}
                >
                  <PolygonDiagram
                    key={`${inspectionItems[0].inspection_item_id}--plane_diagram`}
                    imageId={`${inspectionItems[0].inspection_item_id}--plane_diagram`}
                    width={DIAGRAM_WIDTH}
                    height={350}
                    upperPolygon={topPlane || undefined}
                    lowerPolygon={bottomPlane}
                    cameraProfile={inspectionArea?.camera_profile}
                    inspectionItem={inspectionItems[0]}
                    settings={settings}
                    showEdgeMeasurement
                  />
                </Box>
              </Flex>
            )}

            {/* Polyline diagram  */}
            {polylineLength?.polyline_length && (
              <Flex justifyContent="center" bg="white" alignSelf="stretch">
                <Box
                  className={[
                    'column',
                    'diagram',
                    !settings.sheet_diagram_visibility?.polyline_diagram ? 'hidden' : '',
                  ].join(' ')}
                >
                  <PolylineDiagram
                    key={`${inspectionItems[0].inspection_item_id}--polyline_diagram`}
                    imageId={`${inspectionItems[0].inspection_item_id}--polyline_diagram`}
                    width={DIAGRAM_WIDTH}
                    height={350}
                    inspectionItem={polylineLength}
                    cameraProfile={inspectionArea?.camera_profile}
                  />
                </Box>
              </Flex>
            )}

            {/* Spacer */}
            <Box flex={1} alignSelf="stretch" />
          </Flex>

          {/* Grid values */}
          {grid?.grid &&
          settings.sheet_diagram_visibility?.grid_diagram &&
          shapes.polygons.find(
            (shape) => grid.shape_ids.polygons.includes(shape.shape_id) && shape.plane_side === PlaneSide.LOWER,
          ) ? (
            <GridRow
              inspectionItem={grid}
              imageId={`${inspectionItems[0].inspection_item_id}--grid_diagram`}
              toleranceType={settings.sheet_rows_tolerance_type?.grid || DEFAULT_TOLERANCE_TYPE}
              settings={settings}
              allAdditionalMetricsToggle={allAdditionalMetricsToggle}
              cameraProfile={inspectionArea.camera_profile}
              lowerPolygon={
                shapes.polygons.find(
                  (shape) => grid.shape_ids.polygons.includes(shape.shape_id) && shape.plane_side === PlaneSide.LOWER,
                )!
              }
              upperPolygon={shapes.polygons.find(
                (shape) => grid.shape_ids.polygons.includes(shape.shape_id) && shape.plane_side === PlaneSide.UPPER,
              )}
              inspectionArea={inspectionArea}
              shapes={shapes}
            />
          ) : null}
        </Flex>
      </Flex>
    </Box>
  )
}

export default ModeledItem
